/**
 * API response normalization utilities
 * Handles inconsistent API response structures
 */

/**
 * Extract array data from API response
 * @param {object} data - API response data
 * @param {string[]} keys - Possible keys to check (in order of preference)
 * @returns {array} Extracted array or empty array
 */
const extractArray = (data, keys) => {
  if (!data) return [];

  for (const key of keys) {
    const value = data[key] || data?.data?.[key];
    if (Array.isArray(value)) return value;
  }

  return [];
};

/**
 * Extract frameworks from API response
 * @param {object} data - API response data
 * @returns {array} Array of frameworks
 */
export const extractFrameworks = (data) => {
  return extractArray(data, ["frameworks"]);
};

/**
 * Extract disclosures from API response
 * @param {object} data - API response data
 * @returns {array} Array of disclosures
 */
export const extractDisclosures = (data) => {
  return extractArray(data, ["disclosures", "items"]);
};

/**
 * Extract targets from API response
 * @param {object} data - API response data
 * @returns {array} Array of targets
 */
export const extractTargets = (data) => {
  return extractArray(data, ["targets", "items"]);
};

/**
 * Extract deleted count from API response
 * @param {object} data - API response data
 * @returns {number} Count of deleted items
 */
export const extractDeletedCount = (data) => {
  return data?.deletedCount ?? data?.data?.deletedCount ?? 0;
};

/**
 * Normalize framework object
 * @param {object|string} item - Framework item (object or string)
 * @returns {object|null} Normalized framework or null
 */
export const normalizeFramework = (item) => {
  if (typeof item === "string") {
    return { code: item, name: item };
  }

  const code = item?.code || item?.name || item?.id;
  if (!code) return null;

  return {
    ...item,
    code,
    name: item.name || code,
  };
};

/**
 * Normalize number values (convert empty strings and null to 0)
 * @param {object} obj - Object with numeric values
 * @returns {object} Object with normalized numbers
 */
export const normalizeNumbers = (obj = {}) => {
  return Object.fromEntries(
    Object.entries(obj).map(([key, value]) => [
      key,
      value === "" || value == null ? 0 : Number(value),
    ]),
  );
};

/**
 * Extract data payload from nested response
 * @param {object} data - API response data
 * @returns {object} Extracted data object
 */
export const extractDataPayload = (data) => {
  return data?.data ?? data ?? {};
};
